import { get as _get, identity as _identity, noop as _noop } from 'lodash'

export const DEFAULT_ACCORDION_ACTIONS = {
  registerAccordion: _noop,
  unregisterAccordion: _noop,
  enableAllAccordions: _noop,
  disableAllAccordions: _noop,
  toggleIsAccordionOpen: _noop,
  toggleIsAccordionEnabled: _noop,
}

export enum Actions {
  Register = 'register',
  Unregister = 'unregister',
  EnableAll = 'enable_all',
  DisableAll = 'disable_all',
  ToggleOpen = 'toggle_open',
  ToggleEnabled = 'toggle_enabled',
  Default = 'default',
}

export type AccordionState = {
  isAccordionOpen: boolean
  isAccordionEnabled: boolean
}

export type AccordionReducer = Record<string, AccordionState>

export interface AccordionActions {
  registerAccordion(initialState: AccordionState): void
  unregisterAccordion(): void
  enableAllAccordions(): void
  disableAllAccordions(): void
  toggleIsAccordionOpen(): void
  toggleIsAccordionEnabled(): void
}

interface BaseAction {
  type: Actions
}

interface PayloadAction<P> extends BaseAction {
  payload: P
}

interface RegisterActionPayload {
  key: string
  initialState: AccordionState
}

const DEFAULT_ACCORDION_STATE = {
  isAccordionOpen: false,
  isAccordionEnabled: false,
}

const createRegisterAction = (
  key: string,
  initialState: AccordionState = DEFAULT_ACCORDION_STATE
): PayloadAction<RegisterActionPayload> => ({
  type: Actions.Register,
  payload: { key, initialState },
})

const createUnregisterAction = (key: string): PayloadAction<string> => ({
  type: Actions.Unregister,
  payload: key,
})

const createEnableAllAction = (): BaseAction => ({
  type: Actions.EnableAll,
})

const createDisableAllAction = (): BaseAction => ({
  type: Actions.DisableAll,
})

const createToggleEnabledAction = (key: string): PayloadAction<string> => ({
  type: Actions.ToggleEnabled,
  payload: key,
})

const createToggleOpenAction = (key: string): PayloadAction<string> => ({
  type: Actions.ToggleOpen,
  payload: key,
})

const registerAccordionByKey = (
  state: AccordionReducer,
  action: PayloadAction<RegisterActionPayload>
): AccordionReducer => {
  const { key, initialState } = action.payload
  const previousStateForKey = state[key]

  if (previousStateForKey) {
    return state
  }

  return {
    ...state,
    [key]: { ...initialState },
  }
}

const unregisterAccordionByKey = (
  state: AccordionReducer,
  action: PayloadAction<string>
): AccordionReducer => {
  const key = action.payload
  const previousStateForKey = state[key]

  if (!previousStateForKey) {
    return state
  }

  return Object.keys(state).reduce((nextState, currentKey) => {
    if (key === currentKey) {
      return nextState
    }

    nextState[key] = state[key]

    return nextState
  }, {})
}

const enableAllAccordions = (state: AccordionReducer): AccordionReducer => {
  return Object.keys(state).reduce((nextState, currentKey) => {
    const currentState = state[currentKey]
    nextState[currentKey] = {
      ...currentState,
      isAccordionEnabled: true,
    }

    return nextState
  }, {})
}

const disableAllAccordions = (state: AccordionReducer): AccordionReducer => {
  return Object.keys(state).reduce((nextState, currentKey) => {
    const currentState = state[currentKey]
    nextState[currentKey] = {
      ...currentState,
      isAccordionEnabled: false,
    }

    return nextState
  }, {})
}

const toggleIsAccordionOpenByKey = (
  state: AccordionReducer,
  action: PayloadAction<string>
): AccordionReducer => {
  const key = action.payload
  const previousStateForKey = state[key]
  if (!previousStateForKey) {
    return state
  }

  const nextState = { ...state }
  nextState[key] = {
    ...previousStateForKey,
    isAccordionOpen: !previousStateForKey.isAccordionOpen,
  }

  return nextState
}

const toggleIsAccordionEnabledByKey = (
  state: AccordionReducer,
  action: PayloadAction<string>
): AccordionReducer => {
  const key = action.payload
  const previousStateForKey = state[key]
  if (!previousStateForKey) {
    return state
  }

  const nextState = { ...state }
  nextState[key] = {
    ...previousStateForKey,
    isAccordionEnabled: !previousStateForKey.isAccordionEnabled,
  }

  return nextState
}

const reducerMap = {
  [Actions.Register]: registerAccordionByKey,
  [Actions.Unregister]: unregisterAccordionByKey,
  [Actions.EnableAll]: enableAllAccordions,
  [Actions.DisableAll]: disableAllAccordions,
  [Actions.ToggleEnabled]: toggleIsAccordionEnabledByKey,
  [Actions.ToggleOpen]: toggleIsAccordionOpenByKey,
  [Actions.Default]: _identity,
}

export const buildGlobalAccordionActions = (dispatch) => ({
  enableAllAccordions: () => dispatch(createEnableAllAction()),
  disableAllAccordions: () => dispatch(createDisableAllAction()),
})

export const buildGetActionsForAccordion = (
  dispatch,
  key: string
): AccordionActions => ({
  registerAccordion: (initialState) =>
    dispatch(createRegisterAction(key, initialState)),
  unregisterAccordion: () => dispatch(createUnregisterAction(key)),
  enableAllAccordions: () => dispatch(createEnableAllAction()),
  disableAllAccordions: () => dispatch(createDisableAllAction()),
  toggleIsAccordionOpen: () => dispatch(createToggleOpenAction(key)),
  toggleIsAccordionEnabled: () => dispatch(createToggleEnabledAction(key)),
})

const sectionAccordionReducer = (
  state: AccordionReducer,
  action
): AccordionReducer =>
  _get(reducerMap, action.type, Actions.Default)(state, action)

export default sectionAccordionReducer
